import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import {
  Sparkles, ArrowRight, Wrench, Globe2,
  ShoppingCart, BarChart3, FileText, Check, Upload, Cpu,
  Shield, ChevronDown, Eye, Calculator, FileCode2, Database, Workflow, Send
} from "lucide-react";
import BrandIcon from "../components/app/BrandIcon";
import LanguageSwitcher from "../components/app/LanguageSwitcher";
import HeroStepSpinner from "../components/app/HeroStepSpinner";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { useLangPrefix } from "../lib/langRoutes";
import { usePublicPaths } from "../lib/localizedSlugs";
import { API } from "../lib/api";

/* Live metrics for the hero flange — computed by the real quoting engine. */
function useHeroDemo() {
  const [demo, setDemo] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${API}/public/hero-demo`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setDemo(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return demo;
}

/* ---------- Animated counter ---------- */
function Counter({ end, suffix = "", duration = 1400 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const step = (now) => {
            const p = Math.min(1, (now - start) / duration);
            setVal(Math.floor(p * end));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.4 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{val.toLocaleString("it-IT")}{suffix}</span>;
}

/* ---------- FAQ row ---------- */
function FaqRow({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-slate-800/80">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between py-5 text-left group"
        data-testid={`faq-${idx}`}
      >
        <span className="font-display text-slate-100 text-base lg:text-lg group-hover:text-blue-300 transition-colors">{q}</span>
        <ChevronDown
          size={18}
          className={`text-slate-400 transition-transform duration-300 ${open ? "rotate-180 text-blue-400" : ""}`}
        />
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100 pb-5" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden text-slate-400 text-sm leading-relaxed pr-8">{a}</div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { t } = useTranslation();
  useDocumentMeta();
  const lp = useLangPrefix();
  const pp = usePublicPaths();
  const demo = useHeroDemo();
  const [showBreakdown, setShowBreakdown] = useState(false);

  const plans = [
    {
      key: "free",
      plan: t("landing.pricing.free"),
      price: "€0",
      feats: t("landing.pricing.feat.free", { returnObjects: true }),
      cta: t("landing.pricing.ctaFree"),
    },
    {
      key: "starter",
      plan: t("landing.pricing.starter"),
      price: "€19",
      feats: t("landing.pricing.feat.starter", { returnObjects: true }),
      cta: t("landing.pricing.cta"),
    },
    {
      key: "pro",
      plan: t("landing.pricing.pro"),
      price: "€49",
      feats: t("landing.pricing.feat.pro", { returnObjects: true }),
      cta: t("landing.pricing.cta"),
      highlight: true,
    },
    {
      key: "proPlus",
      plan: t("landing.pricing.proPlus"),
      price: "€99",
      feats: t("landing.pricing.feat.proPlus", { returnObjects: true }),
      cta: t("landing.pricing.cta"),
    },
  ];

  const faqs = [
    { q: t("landing.faq.q1"), a: t("landing.faq.a1") },
    { q: t("landing.faq.q2"), a: t("landing.faq.a2") },
    { q: t("landing.faq.q3"), a: t("landing.faq.a3") },
    { q: t("landing.faq.q4"), a: t("landing.faq.a4") },
  ];

  return (
    <div className="theme-swiss min-h-screen bg-slate-950 text-slate-100 overflow-x-hidden">
      {/* BETA TEST BANNER */}
      <div className="swiss-invert text-white text-sm py-2.5">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-2 text-center flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/15 border border-white/20 text-[10px] font-mono uppercase tracking-wider">
            <Sparkles size={11}/> Beta
          </span>
          <span className="font-medium" data-testid="landing-beta-banner-text"><Trans i18nKey="landing.betaBanner" components={{ strong: <strong /> }} /></span>
        </div>
      </div>

      {/* NAV */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-slate-950/80 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to={`${lp}/`} className="flex items-center gap-2" data-testid="landing-logo">
            <BrandIcon size={32} className="drop-shadow-[0_0_14px_rgba(232,89,12,0.45)]" />
            <div className="font-display font-bold text-slate-100">{t("app.name")}</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-400">
            <a href="#how" className="hover:text-white transition-colors">{t("landing.nav.how")}</a>
            <a href="#capabilities" className="hover:text-white transition-colors">{t("landing.nav.capabilities")}</a>
            <a href="#pricing" className="hover:text-white transition-colors">{t("landing.nav.pricing")}</a>
            <Link to="/blog" className="hover:text-white transition-colors" data-testid="nav-guides-link">{t("landing.nav.guides")}</Link>
            <a href="#faq" className="hover:text-white transition-colors">{t("landing.nav.faq")}</a>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact/>
            <Link to="/login" className="px-4 py-2 rounded-md text-sm text-slate-300 hover:text-white" data-testid="landing-login">{t("landing.ctaSecondary")}</Link>
            <Link to="/register" className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2" data-testid="landing-cta">
              {t("landing.cta")} <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </header>

      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden border-b border-slate-800/60">
        {/* Industrial grid background — no soft blobs */}
        <div className="absolute inset-0 bg-mc-grid opacity-[0.35]"/>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          {/* Left column — copy & status */}
          <div className="lg:col-span-7">
            {/* Concept positioning tag */}
            <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-cyan-500/30 bg-cyan-500/[0.06] font-mono text-[10px] tracking-[0.22em] uppercase text-cyan-300" data-testid="concept-tag">
              <span className="w-1 h-1 rounded-full bg-cyan-400 pulse-live"/>
              {t("landing.conceptTag")}
            </div>
            {/* SYS_STATE badges */}
            <div className="flex items-center gap-2 mb-7 flex-wrap font-mono text-[10px] tracking-[0.2em] uppercase">
              <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-sm border border-emerald-500/40 bg-emerald-500/[0.05] text-emerald-300" data-testid="sys-state">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-live"/> {t("landing.sysState")}
              </span>
              <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-sm border border-slate-700 bg-slate-900/60 text-slate-400">
                <Sparkles size={10}/> {t("landing.badges.ai")}
              </span>
              <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-sm border border-slate-700 bg-slate-900/60 text-slate-400">
                <Shield size={10}/> {t("landing.badges.gdpr")}
              </span>
            </div>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-white">
              {t("landing.heroTitle1")}<br/>
              <span className="text-cyan-400">{t("landing.heroTitle2")}</span><br/>
              {t("landing.heroTitle3")}
            </h1>
            <p className="mt-7 text-slate-400 text-base lg:text-lg max-w-2xl leading-relaxed">{t("landing.heroSub")}</p>

            <div className="mt-9 flex items-center gap-3 flex-wrap">
              <Link
                to="/register"
                className="px-6 py-3 rounded-sm bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-mono text-sm tracking-wider uppercase flex items-center gap-2 transition-colors shadow-[0_0_24px_rgba(34,211,238,0.25)] hover:shadow-[0_0_32px_rgba(34,211,238,0.45)]"
                data-testid="hero-cta"
              >
                {t("landing.cta")} <ArrowRight size={14}/>
              </Link>
              <Link
                to="/register"
                className="px-6 py-3 rounded-sm border border-slate-700 text-slate-300 hover:border-cyan-500/50 hover:text-cyan-400 font-mono text-sm tracking-wider uppercase flex items-center gap-2 transition-colors"
                data-testid="hero-sample"
              >
                <Upload size={14}/> {t("landing.ctaSample")}
              </Link>
            </div>
            <p className="mt-5 text-xs text-slate-500 font-mono tracking-wide flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-cyan-400 pulse-live"/> {t("landing.noCardRequired")}
            </p>
          </div>

          {/* Right column — Mission Control telemetry panel */}
          <div className="lg:col-span-5 relative">
            <div className="corner-bracket border border-slate-800 bg-slate-950/80 backdrop-blur" data-testid="hero-telemetry">
              {/* Telemetry header bar */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800 bg-slate-900/60">
                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-live"/>
                  <span className="text-cyan-400">{t("landing.telemetry.title")}</span>
                </div>
                <div className="font-mono text-[10px] text-slate-500 tracking-wider">{t("landing.telemetry.live")} · 04:12:08</div>
              </div>

              {/* Rotating STEP model — real in-browser OCCT parsing */}
              <div className="relative aspect-[16/10] overflow-hidden border-b border-slate-800 bg-slate-100">
                <HeroStepSpinner />
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-3 left-3 font-mono text-[9px] text-cyan-300 tracking-[0.2em] bg-slate-950/70 px-2 py-0.5 rounded-sm border border-cyan-500/30">HERO_PART.STEP · AP214</div>
                  <div className="absolute bottom-3 right-3 font-mono text-[9px] text-cyan-300 tracking-[0.2em] bg-slate-950/70 px-2 py-0.5 rounded-sm border border-cyan-500/30">Ø120 · 6×Ø9 · Ø38 H7</div>
                </div>
              </div>

              {/* Dense data grid — live values from the real quoting engine, click to expand ops breakdown */}
              <div
                className={`grid grid-cols-3 divide-x divide-slate-800 font-mono ${demo?.breakdown ? "cursor-pointer hover:bg-slate-900/40 transition-colors" : ""}`}
                onClick={() => demo?.breakdown && setShowBreakdown((v) => !v)}
                data-testid="hero-telemetry-grid"
              >
                <div className="px-3 py-3">
                  <div className="text-[9px] tracking-[0.2em] text-slate-500 uppercase">{t("landing.telemetry.spindleLoad")}</div>
                  <div className="mt-1.5 text-amber-400 text-base" data-testid="hero-spindle-load">{demo ? demo.spindle_load_pct : 84}%</div>
                  <div className="mt-1 h-0.5 w-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-amber-400 transition-[width] duration-700" style={{ width: `${demo ? demo.spindle_load_pct : 84}%` }}/>
                  </div>
                </div>
                <div className="px-3 py-3">
                  <div className="text-[9px] tracking-[0.2em] text-slate-500 uppercase">{t("landing.telemetry.cycleTime")}</div>
                  <div className="mt-1.5 text-slate-200 text-base" data-testid="hero-cycle-time">{demo ? demo.cycle_display : "04m:12s"}</div>
                  <div className="mt-1 text-[9px] text-slate-600">setup {demo ? demo.setup_minutes_total : 42}m · {demo ? demo.part.batch_size : 24} pz</div>
                </div>
                <div className="px-3 py-3">
                  <div className="text-[9px] tracking-[0.2em] text-slate-500 uppercase">{t("landing.telemetry.margin")}</div>
                  <div className="mt-1.5 text-cyan-400 text-base" data-testid="hero-margin">
                    € {(demo ? demo.margin_eur : 1247.5).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="mt-1 text-[9px] text-emerald-400" data-testid="hero-margin-pct">+{demo ? demo.margin_pct : 34.2}%</div>
                </div>
              </div>

              {/* Expandable operations breakdown — real machines & times from the engine */}
              {demo?.breakdown && (
                <button
                  type="button"
                  onClick={() => setShowBreakdown((v) => !v)}
                  className="w-full flex items-center justify-center gap-2 py-1.5 border-t border-slate-800 bg-slate-900/30 font-mono text-[9px] tracking-[0.2em] text-slate-500 uppercase hover:text-cyan-400 transition-colors"
                  data-testid="hero-breakdown-toggle"
                >
                  // OPERATIONS_BREAKDOWN · {demo.breakdown.length} OPS
                  <ChevronDown size={11} className={`transition-transform duration-200 ${showBreakdown ? "rotate-180" : ""}`}/>
                </button>
              )}
              {showBreakdown && demo?.breakdown && (
                <div className="border-t border-slate-800 bg-slate-950/60 font-mono" data-testid="hero-breakdown">
                  {demo.breakdown.map((r, i) => (
                    <div key={r.label} className="flex items-center justify-between gap-3 px-4 py-2 border-b border-slate-800/60 last:border-b-0" data-testid={`hero-breakdown-row-${i}`}>
                      <div className="min-w-0">
                        <div className="text-[10px] text-slate-200 truncate">
                          <span className="text-cyan-400">{String(i + 1).padStart(2, "0")}</span> · {r.notes || r.label}
                        </div>
                        <div className="text-[9px] text-slate-500 truncate">{r.machine}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] text-cyan-400">{r.minutes_per_piece.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} min/pz</div>
                        <div className="text-[9px] text-slate-500">setup {r.setup_minutes}m</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Footer line */}
              <div className="px-4 py-2 border-t border-slate-800 bg-slate-900/40 font-mono text-[9px] text-slate-500 tracking-wider flex items-center justify-between">
                <span>// {t("landing.telemetry.calibrated")}</span>
                <span className="text-cyan-400">PROFITABLE</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== PIPELINE — 5 STEP MISSION FLOW ============== */}
      <section className="border-b border-slate-800/60 bg-slate-950 relative" data-testid="landing-pipeline">
        <div className="absolute inset-0 bg-mc-grid opacity-[0.20]"/>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 lg:py-20">
          <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-cyan-400 uppercase mb-2">// PRODUCTION_FLOW</div>
              <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-white">{t("landing.pipeline.title")}</h2>
              <p className="mt-2 text-sm text-slate-500 max-w-xl">{t("landing.pipeline.subtitle")}</p>
            </div>
          </div>
          <PipelineFlow t={t}/>
        </div>
      </section>

      {/* ============== TRUST STRIP / STATS ============== */}
      <section className="border-b border-slate-800/60 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="text-center font-mono text-[10px] tracking-[0.2em] text-slate-500 mb-6 uppercase">// {t("landing.trust.title")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Stat value={<><Counter end={12480}/>+</>} label={t("landing.trust.stat1")}/>
            <Stat value={<><Counter end={42}/>h</>} label={t("landing.trust.stat2")}/>
            <Stat value={<><Counter end={94}/>%</>} label={t("landing.trust.stat3")}/>
            <Stat value={<><Counter end={99}/>.9%</>} label={t("landing.trust.stat4")}/>
          </div>
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <div className="max-w-2xl mb-12">
          <div className="label-tech text-blue-400 mb-3">01 · PROCESS</div>
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight">{t("landing.how.title")}</h2>
          <p className="mt-4 text-slate-400">{t("landing.how.subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 relative">
          <Step
            n="01"
            icon={<Upload size={22} className="text-blue-400"/>}
            title={t("landing.how.step1Title")}
            body={t("landing.how.step1Body")}
          />
          <Step
            n="02"
            icon={<Cpu size={22} className="text-blue-400"/>}
            title={t("landing.how.step2Title")}
            body={t("landing.how.step2Body")}
          />
          <Step
            n="03"
            icon={<FileText size={22} className="text-blue-400"/>}
            title={t("landing.how.step3Title")}
            body={t("landing.how.step3Body")}
          />
        </div>
      </section>

      {/* ============== FEATURES ============== */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <div className="max-w-2xl mb-12">
          <div className="label-tech text-blue-400 mb-3">02 · CAPABILITIES</div>
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight">{t("landing.features.title")}</h2>
          <p className="mt-4 text-slate-400">{t("landing.features.subtitle")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Feature icon={<Sparkles size={20} className="text-blue-400"/>} title={t("landing.features.vision.title")} body={t("landing.features.vision.body")}/>
          <Feature icon={<Wrench size={20} className="text-blue-400"/>} title={t("landing.features.calibration.title")} body={t("landing.features.calibration.body")}/>
          <Feature icon={<ShoppingCart size={20} className="text-blue-400"/>} title={t("landing.features.marketplace.title")} body={t("landing.features.marketplace.body")}/>
          <Feature icon={<BarChart3 size={20} className="text-blue-400"/>} title={t("landing.features.roi.title")} body={t("landing.features.roi.body")}/>
          <Feature icon={<FileText size={20} className="text-blue-400"/>} title={t("landing.features.pdf.title")} body={t("landing.features.pdf.body")}/>
          <Feature icon={<Globe2 size={20} className="text-blue-400"/>} title={t("landing.features.multi.title")} body={t("landing.features.multi.body")}/>
        </div>
      </section>

      {/* ============== CAPABILITIES BREAKDOWN ============== */}
      <CapabilitiesSection t={t}/>

      {/* ============== PRICING ============== */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <div className="text-center mb-12">
          <div className="label-tech text-blue-400 mb-3">03 · PRICING</div>
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight">{t("landing.pricing.title")}</h2>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto">{t("landing.pricing.subtitle")}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((p) => (
            <div
              key={p.key}
              className={`relative tech-card p-6 flex flex-col transition-all hover:-translate-y-1 hover:border-blue-500/60 ${p.highlight ? "border-blue-500/60 ring-1 ring-blue-500/30 shadow-[0_0_40px_rgba(59,130,246,0.18)]" : ""}`}
              data-testid={`pricing-${p.key}`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] px-3 py-1 rounded-full bg-blue-500 text-white font-medium tracking-wider uppercase">
                  {t("landing.pricing.popular")}
                </div>
              )}
              <div className="label-tech mb-2">{p.plan}</div>
              <div className="font-mono-data text-4xl text-slate-100">
                {p.price}<span className="text-sm text-slate-500">{t("landing.pricing.mo")}</span>
              </div>
              <ul className="mt-5 space-y-2.5 text-sm text-slate-300 flex-1">
                {Array.isArray(p.feats) && p.feats.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={14} className="text-emerald-400 mt-0.5 shrink-0"/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className={`mt-6 block text-center px-4 py-2.5 rounded-md text-sm font-medium transition-all ${p.highlight ? "btn-primary" : "bg-slate-800 text-slate-100 border border-slate-700 hover:border-blue-500 hover:bg-slate-700/50"}`}
                data-testid={`pricing-cta-${p.key}`}
              >
                {p.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* ============== FAQ ============== */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-20 lg:py-24">
        <div className="text-center mb-12">
          <div className="label-tech text-blue-400 mb-3">04 · FAQ</div>
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight">{t("landing.faq.title")}</h2>
        </div>
        <div>
          {faqs.map((f, i) => <FaqRow key={f.q} idx={i} q={f.q} a={f.a}/>)}
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-cyan-500/10 to-indigo-600/20"/>
        <div className="absolute inset-0 bg-grid opacity-[0.08]"/>
        <div className="relative max-w-4xl mx-auto px-6 py-20 lg:py-28 text-center">
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight">{t("landing.finalCta.title")}</h2>
          <p className="mt-5 text-slate-300 text-lg max-w-2xl mx-auto">{t("landing.finalCta.subtitle")}</p>
          <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
            <Link to="/register" className="btn-primary px-7 py-3.5 rounded-md font-medium flex items-center gap-2 text-base shadow-[0_0_40px_rgba(59,130,246,0.4)]" data-testid="final-cta">
              {t("landing.finalCta.cta")} <ArrowRight size={16}/>
            </Link>
            <a href="mailto:support@machinepilot.eu" className="px-6 py-3.5 rounded-md text-sm text-slate-200 border border-slate-700 hover:border-blue-500 transition-colors">
              {t("landing.finalCta.secondary")}
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 pt-10 pb-4 grid grid-cols-2 md:grid-cols-3 gap-8 text-sm">
          <div>
            <div className="label-tech text-slate-500 mb-3">{t("blogUi.tagline")}</div>
            <div className="flex flex-col gap-2 text-slate-400">
              <Link to={`${lp}/blog`} className="hover:text-white" data-testid="footer-blog-link">Blog</Link>
              <Link to={pp.blog("come-calcolare-costo-orario-macchina-cnc")} className="hover:text-white">{t("blogUi.footerG1")}</Link>
              <Link to={pp.blog("come-fare-preventivo-lavorazione-cnc")} className="hover:text-white">{t("blogUi.footerG2")}</Link>
              <Link to={pp.blog("7-errori-margine-preventivi-cnc")} className="hover:text-white">{t("blogUi.footerG3")}</Link>
            </div>
          </div>
          <div>
            <div className="label-tech text-slate-500 mb-3">MachinePilot</div>
            <div className="flex flex-col gap-2 text-slate-400">
              <Link to={pp.seo("software-preventivi-cnc")} className="hover:text-white" data-testid="footer-seo-link">{t("blogUi.footerS1")}</Link>
              <Link to={pp.seo("calcolo-tempi-lavorazione-cnc")} className="hover:text-white">{t("blogUi.footerS2")}</Link>
              <Link to={pp.seo("ai-officine-meccaniche")} className="hover:text-white">{t("blogUi.footerS3")}</Link>
              <Link to={pp.seo("analisi-disegno-tecnico-ai")} className="hover:text-white">{t("blogUi.footerS4")}</Link>
            </div>
          </div>
          <div>
            <div className="label-tech text-slate-500 mb-3">// SETTORI</div>
            <div className="flex flex-col gap-2 text-slate-400">
              <Link to={pp.seo("preventivi-officine-meccaniche")} className="hover:text-white">{t("blogUi.footerS5")}</Link>
              <Link to={pp.seo("software-tornerie-cnc")} className="hover:text-white">{t("blogUi.footerS6")}</Link>
              <Link to={pp.seo("software-fresatura-cnc")} className="hover:text-white">{t("blogUi.footerS7")}</Link>
              <Link to={pp.seo("preventivi-carpenteria-metallica")} className="hover:text-white">{t("blogUi.footerS8")}</Link>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-6 border-t border-slate-800/60 flex items-center justify-between flex-wrap gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <BrandIcon size={24} />
            {t("landing.footer")}
          </div>
          <LanguageSwitcher compact/>
        </div>
      </footer>
    </div>
  );
}

/* ---------- Small atoms ---------- */
function Stat({ value, label }) {
  return (
    <div className="text-center">
      <div className="font-mono text-3xl lg:text-4xl text-cyan-400">{value}</div>
      <div className="mt-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</div>
    </div>
  );
}

/* ---------- Mission Control Pipeline ---------- */
const PIPELINE_NODES = [
  { id: "01", icon: Upload,   tKey: "n1", iconColor: "text-cyan-400" },
  { id: "02", icon: Sparkles, tKey: "n2", iconColor: "text-cyan-400" },
  { id: "03", icon: Cpu,      tKey: "n3", iconColor: "text-cyan-400" },
  { id: "04", icon: BarChart3,tKey: "n4", iconColor: "text-cyan-400" },
  { id: "05", icon: FileText, tKey: "n5", iconColor: "text-cyan-400" },
];

function PipelineFlow({ t }) {
  return (
    <div className="relative">
      {/* Horizontal SVG connectors — desktop only */}
      <svg className="hidden lg:block absolute inset-x-0 top-1/2 -translate-y-1/2 w-full h-2 z-0 pointer-events-none" preserveAspectRatio="none" viewBox="0 0 1000 8">
        <line
          x1="0" y1="4" x2="1000" y2="4"
          stroke="#22d3ee" strokeOpacity="0.45" strokeWidth="1"
          strokeDasharray="4 4"
          className="pipeline-flow-line"
        />
      </svg>

      <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {PIPELINE_NODES.map((n) => {
          const Icon = n.icon;
          return (
            <div
              key={n.id}
              className="group relative bg-slate-950 border border-slate-800 hover:border-cyan-500/50 hover:shadow-[0_0_24px_rgba(34,211,238,0.15)] transition-all p-5 rounded-sm"
              data-testid={`pipeline-node-${n.id}`}
            >
              {/* Corner brackets */}
              <span className="absolute top-0 left-0 w-2.5 h-2.5 border-l border-t border-cyan-400/40"/>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-r border-b border-cyan-400/40"/>

              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] tracking-[0.2em] text-cyan-400/70">[{n.id}]</span>
                <Icon size={14} className={`${n.iconColor} group-hover:scale-110 transition-transform`}/>
              </div>
              <div className="font-display text-sm font-medium text-white tracking-wide">{t(`landing.pipeline.${n.tKey}Title`)}</div>
              <div className="mt-2 font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500">{t(`landing.pipeline.${n.tKey}Metric`)}</div>

              {/* Live indicator on hover */}
              <div className="mt-3 flex items-center gap-1.5 font-mono text-[9px] tracking-wider text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="w-1 h-1 rounded-full bg-cyan-400 pulse-live"/> EXEC
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Step({ n, icon, title, body }) {
  return (
    <div className="relative tech-card p-6 group hover:border-blue-500/60 transition-all">
      <div className="absolute top-4 right-5 font-mono-data text-5xl text-slate-800 group-hover:text-blue-500/30 transition-colors">{n}</div>
      <div className="w-11 h-11 rounded-md bg-blue-500/10 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">{icon}</div>
      <div className="font-display font-semibold text-slate-100 mb-2 text-lg">{title}</div>
      <div className="text-sm text-slate-400 leading-relaxed">{body}</div>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="tech-card p-6 group hover:border-blue-500/60 hover:-translate-y-0.5 transition-all">
      <div className="w-10 h-10 rounded-md bg-blue-500/10 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">{icon}</div>
      <div className="font-display font-semibold text-slate-100 mb-2">{title}</div>
      <div className="text-sm text-slate-400 leading-relaxed">{body}</div>
    </div>
  );
}

/* ---------- Capabilities Section (full breakdown of all features) ---------- */
const PILLAR_DEFS = [
  { key: "ai",       slug: "ai-vision",           Icon: Eye,        accent: "blue"    },
  { key: "pricing",  slug: "pricing-smart",       Icon: Calculator, accent: "cyan"    },
  { key: "gcode",    slug: "gcode-cam",           Icon: FileCode2,  accent: "violet"  },
  { key: "machines", slug: "machines-catalog",    Icon: Database,   accent: "emerald" },
  { key: "workflow", slug: "workflow",            Icon: Workflow,   accent: "amber"   },
  { key: "output",   slug: "output-integrations", Icon: Send,       accent: "rose"    },
];

const ACCENT_MAP = {
  blue:    { border: "border-blue-500/40 hover:border-blue-400",       icon: "text-blue-400",    tag: "text-blue-300",    glow: "shadow-[0_0_24px_rgba(59,130,246,0.12)]"   },
  cyan:    { border: "border-cyan-500/40 hover:border-cyan-400",       icon: "text-cyan-400",    tag: "text-cyan-300",    glow: "shadow-[0_0_24px_rgba(34,211,238,0.12)]"   },
  violet:  { border: "border-violet-500/40 hover:border-violet-400",   icon: "text-violet-400",  tag: "text-violet-300",  glow: "shadow-[0_0_24px_rgba(167,139,250,0.12)]"  },
  emerald: { border: "border-emerald-500/40 hover:border-emerald-400", icon: "text-emerald-400", tag: "text-emerald-300", glow: "shadow-[0_0_24px_rgba(52,211,153,0.12)]"   },
  amber:   { border: "border-amber-500/40 hover:border-amber-400",     icon: "text-amber-400",   tag: "text-amber-300",   glow: "shadow-[0_0_24px_rgba(251,191,36,0.12)]"   },
  rose:    { border: "border-rose-500/40 hover:border-rose-400",       icon: "text-rose-400",    tag: "text-rose-300",    glow: "shadow-[0_0_24px_rgba(244,114,182,0.12)]"  },
};

function CapabilitiesSection({ t }) {
  const pp = usePublicPaths();
  return (
    <section id="capabilities" className="relative border-y border-slate-800/60 bg-slate-950" data-testid="capabilities-section">
      <div className="absolute inset-0 bg-mc-grid opacity-[0.18] pointer-events-none"/>
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 lg:py-24">
        {/* Section header */}
        <div className="max-w-3xl mb-12">
          <div className="label-tech text-cyan-400 mb-3">{t("landing.capabilities.sectionTag")}</div>
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight text-white">{t("landing.capabilities.title")}</h2>
          <p className="mt-4 text-slate-400 text-base lg:text-lg leading-relaxed">{t("landing.capabilities.subtitle")}</p>
        </div>

        {/* Stats strip */}
        <div className="mb-12 tech-card p-6">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-4">// {t("landing.capabilities.statsLabel")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <CapStat value="20+" label={t("landing.capabilities.stats.modules")}/>
            <CapStat value="214" label={t("landing.capabilities.stats.machines")}/>
            <CapStat value="32"  label={t("landing.capabilities.stats.materials")}/>
            <CapStat value="6"   label={t("landing.capabilities.stats.languages")}/>
          </div>
        </div>

        {/* 6 pillars grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLAR_DEFS.map(({ key, slug, Icon, accent }) => {
            const cls = ACCENT_MAP[accent];
            const items = t(`landing.capabilities.pillars.${key}.items`, { returnObjects: true });
            return (
              <Link
                key={key}
                to={pp.cap(slug)}
                className={`tech-card p-6 group transition-all ${cls.border} hover:${cls.glow} hover:-translate-y-0.5 block focus:outline-none focus:ring-2 focus:ring-cyan-500/40`}
                data-testid={`capability-pillar-${key}`}
                aria-label={`${t(`landing.capabilities.pillars.${key}.title`)} — vai al dettaglio`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`font-mono text-[9px] tracking-[0.22em] uppercase ${cls.tag}`}>
                    {t(`landing.capabilities.pillars.${key}.tag`)}
                  </span>
                  <Icon size={16} className={`${cls.icon} group-hover:scale-110 transition-transform`} aria-hidden="true"/>
                </div>
                <div className="font-display font-semibold text-slate-100 text-lg mb-4 tracking-tight">
                  {t(`landing.capabilities.pillars.${key}.title`)}
                </div>
                <ul className="space-y-2.5">
                  {Array.isArray(items) && items.map((line, idx) => (
                    <li key={`${key}-${idx}`} className="flex items-start gap-2 text-sm text-slate-300 leading-relaxed">
                      <Check size={13} className={`${cls.icon} mt-0.5 shrink-0`} aria-hidden="true"/>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <div className={`mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between font-mono text-[10px] tracking-[0.2em] uppercase ${cls.tag} opacity-70 group-hover:opacity-100 transition-opacity`}>
                  <span>{t("landing.capabilities.learnMore")}</span>
                  <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform"/>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Bottom note */}
        <div className="mt-10 text-center font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500" data-testid="capabilities-footer">
          // {t("landing.capabilities.footer")}
        </div>
      </div>
    </section>
  );
}

function CapStat({ value, label }) {
  return (
    <div className="text-center">
      <div className="font-mono-data text-3xl text-cyan-400 leading-none">{value}</div>
      <div className="mt-2 font-mono text-[9px] tracking-[0.2em] uppercase text-slate-500">{label}</div>
    </div>
  );
}
